// src/components/admin/command_center/tabs/SongLibraryTab.jsx
// NSL Song Library CRM — Sprint 2
// Full CRUD: list + search/filter + edit modal + duplicate + delete

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './SongLibraryTab.css';

// ══════════════════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════
//  LO QUE DIJO EL SERVIDOR

//
//  Aqui todos los errores se traducian a un «❌ Error al guardar» o un
//  «❌ Error al eliminar». Eso tira la unica informacion util: el backend
//  responde 409 diciendo con que cancion choca, y 422 explicando que un
//  `youtube_id` mal pegado se arregla copiando solo lo que va detras de «v=».
//  Convertir eso en «Error» manda al que lo lee a adivinar.
// ══════════════════════════════════════════════════════════════════════
async function loQueDijo(res, porDefecto) {
    try {
        const d = await res.json();
        if (d && d.detail) return String(d.detail);
    } catch { /* la respuesta no era JSON */ }
    return `${porDefecto} (HTTP ${res.status}).`;
}

const DIFFICULTIES = ['basic', 'intermediate', 'advanced', 'expert'];
const CATEGORIES = ['pop', 'rock', 'soul', 'indie', 'classic', 'country', 'electronic', 'folk'];

const DIFF_CFG = {
    basic: { label: 'Básico', emoji: '🟢', color: '#4ade80' },
    intermediate: { label: 'Intermedio', emoji: '🔵', color: '#60a5fa' },
    advanced: { label: 'Avanzado', emoji: '🟠', color: '#fb923c' },
    expert: { label: 'Experto', emoji: '🔴', color: '#f87171' },
};

const CAT_EMOJI = {
    pop: '🎵', rock: '🎸', soul: '🎷', indie: '🌙',
    classic: '🏛️', country: '🤠', electronic: '⚡', folk: '🎻',
};

// ══════════════════════════════════════════════════════════════════════
//  TRANSCRIPT PARSER  — mirror exacto de transcript_parser.py
//
//  SOPORTA:
//    • M:SS          →  0:14  /  1:02
//    • H:MM:SS       →  1:02:30
//    • Inline        →  0:1414 seconds♪ Here comes...
//    • Multilínea    →  0:14 \n 14 seconds \n ♪ Here comes...
//    • LRC centisecs →  [01:14.35]  (opcional, para futuros formatos)
//
//  GARANTÍA DE TIEMPO:
//    • time_s  → SIEMPRE Number entero (Math.trunc en cada componente)
//    • time_ms → milisegundos exactos si el formato los provee, sino time_s * 1000
//    • Cero aritmética float en la conversión de horas/min/seg
//    • Orden monotónico garantizado (sort final)
//    • Sin duplicados (mismo time_ms → fusión de textos)
// ══════════════════════════════════════════════════════════════════════

const _TS_RE = /^(\d+):(\d{2})(?::(\d{2}))?/;
const _LRC_RE = /^\[(\d+):(\d{2})\.(\d{2,3})\]/;
const _DUR_LINE = /^\d+\s+(?:minute|second)s?(?:\s*,\s*\d+\s+(?:minute|second)s?)?$/i;
const _DUR_PREFIX = /^\d+\s+(?:minute|second)s?(?:\s*,\s*\d+\s+(?:minute|second)s?)?\s*/i;
const _NOISE = /[♪♫]+/g;
const _MULTI_WS = /\s{2,}/g;

function _tsToMs(m) {
    if (m[3] !== undefined) {
        const s = Math.trunc(+m[1]) * 3600 + Math.trunc(+m[2]) * 60 + Math.trunc(+m[3]);
        return { s, ms: s * 1000 };
    }
    const s = Math.trunc(+m[1]) * 60 + Math.trunc(+m[2]);
    return { s, ms: s * 1000 };
}

function _lrcToMs(m) {
    const min = Math.trunc(+m[1]);
    const sec = Math.trunc(+m[2]);
    const raw = m[3];
    const ms = raw.length === 3 ? Math.trunc(+raw) : Math.trunc(+raw) * 10;
    const totalMs = (min * 60 + sec) * 1000 + ms;
    return { s: Math.trunc(totalMs / 1000), ms: totalMs };
}

function _clean(text) {
    return text.replace(_NOISE, ' ').replace(_MULTI_WS, ' ').trim();
}

export function parseTranscript(raw) {
    const lines = raw
        .replace(/^\uFEFF/, '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n')
        .map(l => l.trim());

    const result = [];
    let curTime = null;
    let pending = [];

    function flush() {
        if (curTime && pending.length) {
            const clean = _clean(pending.join(' '));
            if (clean) {
                result.push({ time_s: curTime.s, time_ms: curTime.ms, text: clean });
            }
        }
        pending = [];
    }

    for (const line of lines) {
        if (!line) continue;

        const lrcMatch = line.match(_LRC_RE);
        if (lrcMatch) {
            flush();
            curTime = _lrcToMs(lrcMatch);
            const rest = _clean(line.slice(lrcMatch[0].length));
            if (rest) pending.push(rest);
            continue;
        }

        const tsMatch = line.match(_TS_RE);
        if (tsMatch) {
            flush();
            curTime = _tsToMs(tsMatch);
            let rest = line.slice(tsMatch[0].length).trim();
            if (!rest) continue;
            rest = rest.replace(_DUR_PREFIX, '').trim();
            if (rest) pending.push(rest);
            continue;
        }

        if (!curTime) continue;
        if (_DUR_LINE.test(line)) continue;
        pending.push(line);
    }
    flush();

    const map = new Map();
    for (const e of result) {
        if (map.has(e.time_ms)) {
            map.get(e.time_ms).text += ' ' + e.text;
        } else {
            map.set(e.time_ms, { ...e });
        }
    }

    return [...map.values()].sort((a, b) => a.time_ms - b.time_ms);
}

function _fmtMs(ms) {
    const totalSec = Math.trunc(ms / 1000);
    const rem = ms % 1000;
    const m = Math.trunc(totalSec / 60);
    const s = totalSec % 60;
    return rem > 0
        ? `${m}:${String(s).padStart(2, '0')}.${String(rem).padStart(3, '0')}`
        : `${m}:${String(s).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────
//  TRANSCRIPT IMPORT PANEL
// ─────────────────────────────────────────────────────────────────────

function TranscriptImportPanel({ song, token, onImported }) {
    const [raw, setRaw] = useState('');
    const [preview, setPreview] = useState(null);
    const [status, setStatus] = useState('idle');
    const [errMsg, setErrMsg] = useState('');
    const [overwrite, setOverwrite] = useState(false);
    const taRef = useRef(null);

    const hasExisting = !!(song.sync_data?.lines?.length);

    const handleParse = useCallback(() => {
        if (!raw.trim()) return;
        setStatus('parsing');
        setTimeout(() => {
            try {
                const entries = parseTranscript(raw);
                setPreview(entries);
                if (entries.length < 2) {
                    setStatus('error');
                    setErrMsg(`Solo se encontraron ${entries.length} entradas. Verifica el formato.`);
                } else {
                    setStatus('idle');
                    setErrMsg('');
                }
            } catch {
                setPreview(null);
                setStatus('error');
                setErrMsg('Error al parsear. Verifica que sea un transcript de YouTube.');
            }
        }, 0);
    }, [raw]);

    const handleSave = async () => {
        if (!preview || preview.length < 2) return;
        setStatus('saving');
        setErrMsg('');
        try {
            const authToken = token || localStorage.getItem('token');
            const res = await fetch('/api/minigames/cinema/save-transcript', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    raw_transcript: raw,
                    song_id: song.id,
                    overwrite,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setStatus('error');
                setErrMsg(data.detail || 'Error al guardar.');
                return;
            }
            setStatus('ok');
            if (onImported) onImported({ parsed_count: data.parsed_count });
        } catch {
            setStatus('error');
            setErrMsg('Error de red. Verifica la conexión.');
        }
    };

    const canParse = raw.trim().length > 10 && status !== 'saving';
    const canSave = preview?.length >= 2 && status === 'idle';
    const isOk = status === 'ok';

    const hasMs = preview?.some(e => e.time_ms % 1000 !== 0);
    const isMonotonic = preview?.every((e, i) => i === 0 || e.time_ms > preview[i - 1].time_ms) ?? true;

    return (
        <div className="sl-transcript-panel">
            <div className="sl-tr-instr">
                <div className="sl-tr-instr-title">¿Cómo copiar el transcript de YouTube?</div>
                <ol className="sl-tr-steps">
                    <li>Abre el video → <kbd>···</kbd> → <kbd>Mostrar transcripción</kbd></li>
                    <li>Selecciona todo (<kbd>Ctrl+A</kbd>) y copia (<kbd>Ctrl+C</kbd>)</li>
                    <li>Pega abajo y presiona <strong>Parsear</strong></li>
                </ol>
                <div className="sl-tr-formats">
                    Soporta: <code>0:14</code> multilínea · <code>0:1414 seconds</code> inline ·
                    <code>1:02:30</code> H:MM:SS · <code>[01:14.35]</code> LRC con milisegundos
                </div>
            </div>

            <div className="sl-tr-field">
                <label className="sl-tr-label">
                    Texto del transcript
                    {raw.length > 0 && <span className="sl-tr-chars">{raw.length.toLocaleString()} chars</span>}
                </label>
                <textarea
                    ref={taRef}
                    className="sl-tr-textarea"
                    value={raw}
                    rows={7}
                    spellCheck={false}
                    placeholder={"0:14\n14 seconds\n♪ Here comes the sun ♪\n0:21\n21 seconds\n♪ It's all right ♪\n..."}
                    onChange={e => {
                        setRaw(e.target.value);
                        if (preview) { setPreview(null); setStatus('idle'); setErrMsg(''); }
                    }}
                />
            </div>

            <div className="sl-tr-row">
                <button
                    className="sl-tr-btn-parse"
                    disabled={!canParse}
                    onClick={handleParse}
                >
                    {status === 'parsing' ? '⏳ Parseando...' : '🔍 Parsear y previsualizar'}
                </button>
                {raw && (
                    <button
                        className="sl-tr-btn-clear"
                        onClick={() => { setRaw(''); setPreview(null); setStatus('idle'); setErrMsg(''); }}
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {status === 'error' && errMsg && (
                <div className="sl-tr-error">⚠️ {errMsg}</div>
            )}

            {preview && preview.length >= 2 && (
                <div className="sl-tr-preview">
                    <div className="sl-tr-statsbar">
                        <div className="sl-tr-stat">
                            <span className="sl-tr-stat-n">{preview.length}</span>
                            <span className="sl-tr-stat-l">entradas</span>
                        </div>
                        <div className="sl-tr-stat">
                            <span className="sl-tr-stat-n" style={{ color: '#34d399' }}>
                                {_fmtMs(preview[0].time_ms)}
                            </span>
                            <span className="sl-tr-stat-l">primer tiempo</span>
                        </div>
                        <div className="sl-tr-stat">
                            <span className="sl-tr-stat-n" style={{ color: '#a78bfa' }}>
                                {_fmtMs(preview[preview.length - 1].time_ms)}
                            </span>
                            <span className="sl-tr-stat-l">último tiempo</span>
                        </div>
                        <div className="sl-tr-stat">
                            <span className="sl-tr-stat-n" style={{ color: isMonotonic ? '#34d399' : '#f87171' }}>
                                {isMonotonic ? '✓' : '⚠'}
                            </span>
                            <span className="sl-tr-stat-l">monotónico</span>
                        </div>
                        <div className="sl-tr-stat">
                            <span className="sl-tr-stat-n" style={{ color: '#60a5fa' }}>
                                {hasMs ? 'ms' : 's'}
                            </span>
                            <span className="sl-tr-stat-l">precisión</span>
                        </div>
                    </div>

                    <div className="sl-tr-guarantee">
                        ✅ Todos los tiempos son <code>int</code> exactos — cero float, cero drift.
                        {hasMs && ' Milisegundos capturados correctamente.'}
                    </div>

                    <div className="sl-tr-table-wrap">
                        <table className="sl-tr-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>time_ms</th>
                                    <th>MM:SS{hasMs ? '.mmm' : ''}</th>
                                    <th>Texto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.slice(0, 60).map((e, i) => (
                                    <tr key={e.time_ms} className={i % 2 === 0 ? 'sl-tr-even' : ''}>
                                        <td className="sl-tr-idx">{i + 1}</td>
                                        <td className="sl-tr-ms"><code>{e.time_ms}</code></td>
                                        <td className="sl-tr-fmt">
                                            <span className="sl-tr-ts-pill">{_fmtMs(e.time_ms)}</span>
                                        </td>
                                        <td className="sl-tr-text">{e.text}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {preview.length > 60 && (
                            <div className="sl-tr-more">+ {preview.length - 60} entradas más</div>
                        )}
                    </div>

                    {hasExisting && (
                        <label className="sl-tr-overwrite">
                            <input
                                type="checkbox"
                                checked={overwrite}
                                onChange={e => setOverwrite(e.target.checked)}
                            />
                            <span>
                                <strong>Sobreescribir</strong> — Esta canción ya tiene{' '}
                                <strong>{song.sync_data.lines.length} líneas</strong>.
                                Se reemplazarán al guardar.
                            </span>
                        </label>
                    )}

                    {!isOk && (
                        <button
                            className="sl-tr-btn-save"
                            onClick={handleSave}
                            disabled={!canSave || (hasExisting && !overwrite)}
                        >
                            {status === 'saving'
                                ? '⏳ Guardando en DB...'
                                : `💾 Guardar ${preview.length} entradas en sync_data`}
                        </button>
                    )}

                    {isOk && (
                        <div className="sl-tr-success">
                            <span className="sl-tr-success-icon">✅</span>
                            <div>
                                <strong>sync_data guardado correctamente</strong><br />
                                {preview.length} entradas con tiempos exactos.
                                Abre el Sync Editor para agregar blancos.
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════════════

const thumb = (ytId) =>
    `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`;

const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('es-MX', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
};

const countBlanks = (syncData) => {
    if (!syncData?.lines) return 0;
    return syncData.lines.reduce((acc, l) => acc + (l.blanks?.length || 0), 0);
};

// ══════════════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ══════════════════════════════════════════════════════════════════════

function DiffBadge({ diff }) {
    const cfg = DIFF_CFG[diff] || DIFF_CFG.intermediate;
    return (
        <span className="sl-badge" style={{ '--bc': cfg.color }}>
            {cfg.emoji} {cfg.label}
        </span>
    );
}

function ConfirmModal({ song, onConfirm, onCancel }) {
    return (
        <div className="sl-overlay" onClick={onCancel}>
            <div className="sl-confirm-box" onClick={e => e.stopPropagation()}>
                <div className="sl-confirm-icon">🗑️</div>
                <h3>Eliminar canción</h3>
                <p>
                    ¿Seguro que quieres eliminar <strong>"{song.title}"</strong> de{' '}
                    <strong>{song.artist}</strong>? Esta acción no se puede deshacer.
                </p>
                <div className="sl-confirm-btns">
                    <button className="sl-btn-ghost" onClick={onCancel}>Cancelar</button>
                    <button className="sl-btn-danger" onClick={onConfirm}>Sí, eliminar</button>
                </div>
            </div>
        </div>
    );
}

function EditModal({ song, token, onSave, onClose, onTranscriptImported }) {
    const [form, setForm] = useState({
        title: song.title,
        artist: song.artist,
        difficulty: song.difficulty,
        category: song.category,
        youtube_id: song.youtube_id,
    });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [activeTab, setActiveTab] = useState('meta');

    const blanks = countBlanks(song.sync_data);
    const lines = song.sync_data?.lines?.length || 0;
    const stamped = song.sync_data?.lines?.filter(l => l.time > 0).length || 0;

    const handleSave = async () => {
        setSaving(true);
        setSaveError('');
        try {
            const authToken = token || localStorage.getItem('token');
            // ── SOLO LOS METADATOS ──────────────────────────────────
            // Antes iba `{ ...form, sync_data: song.sync_data }`: para cambiar
            // el titulo se reenviaba la sincronizacion entera. Si la copia que
            // tenia esta pantalla estaba vieja —porque alguien la edito en el
            // Sync Editor mientras tanto— se pisaban horas de trabajo.
            //
            // El PUT es un parche desde hoy: lo que no se manda, no se toca.
            const res = await fetch(`/api/admin/sync-editor/${song.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify(form),
            });
            if (!res.ok) {
                // El mensaje del servidor tal cual: un 422 explica que un
                // `youtube_id` mal pegado se arregla copiando lo que va detras
                // de «v=», y eso es accionable. «Error al guardar» no.
                setSaveError(`❌ ${await loQueDijo(res, 'No se pudo guardar')}`);
                setSaving(false);
                return;
            }
            const updated = await res.json();
            onSave(updated);
        } catch {
            setSaveError('❌ No se pudo hablar con el servidor. Nada ha cambiado.');
        }
        setSaving(false);
    };

    const field = (key, label, opts = {}) => (
        <div className="sl-form-field">
            <label>{label}</label>
            {opts.type === 'select' ? (
                <select value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}>
                    {opts.options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            ) : (
                <input
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={opts.placeholder || ''}
                />
            )}
        </div>
    );

    const TABS = [
        { id: 'meta', label: '📝 Metadata' },
        { id: 'transcript', label: '📥 Transcript', dot: !song.sync_data?.lines?.length },
        { id: 'stats', label: '📊 Estadísticas' },
        { id: 'data', label: '🎵 Sync Data' },
    ];

    return (
        <div className="sl-overlay" onClick={onClose}>
            <div className="sl-modal" onClick={e => e.stopPropagation()}>

                {/* Modal header */}
                <div className="sl-modal-head">
                    <div className="sl-modal-thumb">
                        <img src={thumb(song.youtube_id)} alt="" />
                    </div>
                    <div className="sl-modal-title-wrap">
                        <div className="sl-modal-title">{song.title}</div>
                        <div className="sl-modal-sub">{song.artist}</div>
                    </div>
                    <button className="sl-modal-close" onClick={onClose}>✕</button>
                </div>

                {/* Tabs */}
                <div className="sl-modal-tabs">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            className={`sl-modal-tab ${activeTab === t.id ? 'active' : ''} ${t.dot ? 'sl-modal-tab--dot' : ''}`}
                            onClick={() => setActiveTab(t.id)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Tab: Metadata */}
                {activeTab === 'meta' && (
                    <div className="sl-modal-body">
                        {field('youtube_id', 'YouTube ID', { placeholder: 'dQw4w9WgXcQ' })}
                        {field('title', 'Título')}
                        {field('artist', 'Artista')}
                        {field('difficulty', 'Dificultad', {
                            type: 'select',
                            options: DIFFICULTIES.map(d => ({
                                value: d,
                                label: `${DIFF_CFG[d].emoji} ${DIFF_CFG[d].label}`,
                            })),
                        })}
                        {field('category', 'Categoría', {
                            type: 'select',
                            options: CATEGORIES.map(c => ({
                                value: c,
                                label: `${CAT_EMOJI[c] || '🎶'} ${c.charAt(0).toUpperCase() + c.slice(1)}`,
                            })),
                        })}
                    </div>
                )}

                {/* Tab: Transcript Import */}
                {activeTab === 'transcript' && (
                    <div className="sl-modal-body sl-modal-body--noPad">
                        <TranscriptImportPanel
                            song={song}
                            token={token}
                            onImported={(info) => onTranscriptImported?.(song.id, info)}
                        />
                    </div>
                )}

                {/* Tab: Stats */}
                {activeTab === 'stats' && (
                    <div className="sl-modal-body">
                        <div className="sl-stats-grid">
                            <div className="sl-stat-card">
                                <div className="sl-stat-n">{lines}</div>
                                <div className="sl-stat-l">Líneas totales</div>
                            </div>
                            <div className="sl-stat-card">
                                <div className="sl-stat-n" style={{ color: '#34d399' }}>{stamped}</div>
                                <div className="sl-stat-l">Con timestamp</div>
                            </div>
                            <div className="sl-stat-card">
                                <div className="sl-stat-n" style={{ color: '#fb923c' }}>{blanks}</div>
                                <div className="sl-stat-l">Blancos totales</div>
                            </div>
                            <div className="sl-stat-card">
                                <div className="sl-stat-n" style={{ color: '#a78bfa' }}>
                                    {lines > 0 ? Math.round((blanks / lines) * 10) / 10 : 0}
                                </div>
                                <div className="sl-stat-l">Blancos/línea</div>
                            </div>
                        </div>
                        {lines > 0 && (
                            <div className="sl-progress-section">
                                <div className="sl-progress-label">
                                    <span>Timestamps completados</span>
                                    <span>{Math.round((stamped / lines) * 100)}%</span>
                                </div>
                                <div className="sl-progress-bar">
                                    <div
                                        className="sl-progress-fill"
                                        style={{ width: `${(stamped / lines) * 100}%`, '--fc': '#34d399' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Sync Data raw preview */}
                {activeTab === 'data' && (
                    <div className="sl-modal-body">
                        <div className="sl-data-hint">Vista de solo lectura. Edita el contenido sync desde el editor principal.</div>
                        <div className="sl-lines-preview">
                            {(song.sync_data?.lines || []).slice(0, 20).map((line, i) => (
                                <div key={i} className="sl-line-row">
                                    <span className="sl-line-time">{(line.time || 0).toFixed(1)}s</span>
                                    <span className="sl-line-txt">{line.text}</span>
                                    {line.blanks?.length > 0 && (
                                        <span className="sl-line-blanks">{line.blanks.length} 📝</span>
                                    )}
                                </div>
                            ))}
                            {(song.sync_data?.lines?.length || 0) > 20 && (
                                <div className="sl-data-more">
                                    + {song.sync_data.lines.length - 20} líneas más...
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="sl-modal-foot">
                    {saveError && <span className="sl-save-error">{saveError}</span>}
                    <button className="sl-btn-ghost" onClick={onClose}>Cancelar</button>
                    {activeTab === 'meta' && (
                        <button className="sl-btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? '⏳ Guardando...' : '💾 Guardar cambios'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════

export default function SongLibraryTab({ token, abrirEn }) {
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterDiff, setFilterDiff] = useState('');
    const [filterCat, setFilterCat] = useState('');
    const [editSong, setEditSong] = useState(null);
    const [deleteSong, setDeleteSong] = useState(null);
    const [toast, setToast] = useState({ text: '', type: '' });
    const [view, setView] = useState('table'); // 'table' | 'grid'
    //  Qué canción se está abriendo en el Sync Editor. Pedirle la canción
    //  entera al servidor tarda, y sin esto el botón se queda mudo y se pulsa
    //  tres veces.
    const [abriendo, setAbriendo] = useState(null);

    const authToken = token || localStorage.getItem('token');

    const showToast = (text, type = 'info') => {
        setToast({ text, type });
        setTimeout(() => setToast({ text: '', type: '' }), 3500);
    };

    // ─ Fetch songs ───────────────────────────────────────────────────
    const fetchSongs = useCallback(async () => {
        setLoading(true);
        try {
            // Con techo: el servidor lo tiene desde hoy, y pedir sin limite
            // era traerse el catalogo entero con el JSON de cada cancion.
            const res = await fetch('/api/admin/sync-editor/?limit=200', {
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudieron cargar')}`, 'error');
                setLoading(false);
                return;
            }
            const data = await res.json();
            setSongs(data);
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
        setLoading(false);
    }, [authToken]);

    useEffect(() => { fetchSongs(); }, [fetchSongs]);

    // ─ Delete ────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteSong) return;
        try {
            const res = await fetch(`/api/admin/sync-editor/${deleteSong.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudo eliminar')}`, 'error');
                setDeleteSong(null);
                return;
            }
            setSongs(prev => prev.filter(s => s.id !== deleteSong.id));
            showToast(`🗑️ "${deleteSong.title}" eliminada`, 'success');
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
        setDeleteSong(null);
    };

    // ─ Publicar / despublicar ────────────────────────────────────────
    // Despublicar NO destruye: la canción se queda con su sincronización
    // intacta y deja de aparecer en el catálogo del alumno. Es la diferencia
    // entre «esto todavía no» y «esto fuera», y hasta hoy sólo existía la
    // segunda.
    const handlePublicar = async (song) => {
        const quiero = !(song.is_published !== false);
        try {
            const res = await fetch(`/api/admin/sync-editor/${song.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                // Sólo la marca: el PUT es un parche, así que la
                // sincronización ni se toca.
                body: JSON.stringify({ is_published: quiero }),
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudo cambiar')}`, 'error');
                return;
            }
            const actualizada = await res.json();
            setSongs(prev => prev.map(x => x.id === song.id ? actualizada : x));
            showToast(
                quiero
                    ? `🌍 "${song.title}" ya se ve en el catálogo`
                    : `🔒 "${song.title}" fuera del catálogo — sigue guardada`,
                'success',
            );
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
    };

    // ─ Duplicate ─────────────────────────────────────────────────────
    const handleDuplicate = async (song) => {
        try {
            const res = await fetch(`/api/admin/sync-editor/${song.id}/duplicate`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudo duplicar')}`, 'error');
                return;
            }
            const copy = await res.json();
            setSongs(prev => [copy, ...prev]);
            showToast(`📋 Copia de "${song.title}" creada`, 'success');
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
    };

    /**
     * La varita: abre la canción entera en el Sync Editor.
     *
     * Rob, 07-10-2026: «de entrada veo que Song Library no tiene su botón de
     * varita para editar la canción en Sync Editor como lo tenemos en vídeos, y
     * es genial».
     *
     * El lápiz de al lado cambia la FICHA —título, artista, categoría—. Esto
     * abre la canción de verdad: sus tiempos, sus blancos y sus distractores,
     * para corregir una sílaba desfasada sin volver a montarla desde cero.
     *
     * La lista no trae el `sync_data` —serían veinticuatro canciones con todas
     * sus letras en cada carga—, así que se pide la canción entera aquí.
     */
    const corrigeEnElEditor = async (song) => {
        if (!abrirEn) return;
        setAbriendo(song.id);
        try {
            const res = await fetch(`/api/admin/sync-editor/${song.id}`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudo abrir la canción')}`, 'error');
                setAbriendo(null);
                return;
            }
            abrirEn('Sync Editor', { cancion: await res.json() });
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
        setAbriendo(null);
    };

    // ─ Save edit ─────────────────────────────────────────────
    const handleSaveEdit = (updated) => {
        setSongs(prev => prev.map(s => s.id === updated.id ? updated : s));
        setEditSong(null);
        showToast('✅ Cambios guardados', 'success');
    };

    // ─ Transcript imported callback ──────────────────────────────────
    const handleTranscriptImported = useCallback(async (songId, info) => {
        showToast(`📥 ${info.parsed_count} líneas importadas con tiempos exactos`, 'success');
        try {
            const res = await fetch(`/api/admin/sync-editor/${songId}`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) return;
            const updated = await res.json();
            setSongs(prev => prev.map(s => s.id === songId ? updated : s));
            setEditSong(prev => prev?.id === songId ? updated : prev);
        } catch { /* DB guardó OK, solo el cache local queda stale */ }
    }, [authToken]);

    // ─ Filtered list ─────────────────────────────────────────────────
    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        return songs.filter(s => {
            if (q && !s.title.toLowerCase().includes(q) && !s.artist.toLowerCase().includes(q)) return false;
            if (filterDiff && s.difficulty !== filterDiff) return false;
            if (filterCat && s.category !== filterCat) return false;
            return true;
        });
    }, [songs, search, filterDiff, filterCat]);

    // ─ Stats ─────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const byDiff = {};
        for (const s of songs) {
            byDiff[s.difficulty] = (byDiff[s.difficulty] || 0) + 1;
        }
        const totalBlanks = songs.reduce((acc, s) => acc + countBlanks(s.sync_data), 0);
        return { total: songs.length, byDiff, totalBlanks };
    }, [songs]);

    // ══════════════════════════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════════════════════════
    return (
        <div className="sl-root">

            {/* ── HEADER ── */}
            <header className="sl-header">
                <div className="sl-brand">
                    <span className="sl-brand-icon">📚</span>
                    <div>
                        <div className="sl-brand-title">Song Library</div>
                        <div className="sl-brand-sub">NSL CRM · Canciones curadas</div>
                    </div>
                </div>

                {/* Stats pills */}
                <div className="sl-header-stats">
                    <div className="sl-hs-pill">
                        <span className="sl-hs-n">{stats.total}</span>
                        <span className="sl-hs-l">canciones</span>
                    </div>
                    <div className="sl-hs-pill">
                        <span className="sl-hs-n">{stats.totalBlanks}</span>
                        <span className="sl-hs-l">blancos totales</span>
                    </div>
                    {Object.entries(stats.byDiff).map(([d, n]) => (
                        <div key={d} className="sl-hs-badge" style={{ '--bc': DIFF_CFG[d]?.color }}>
                            {DIFF_CFG[d]?.emoji} {n}
                        </div>
                    ))}
                </div>

                <div className="sl-header-actions">
                    <button
                        className={`sl-view-btn ${view === 'table' ? 'active' : ''}`}
                        onClick={() => setView('table')}
                        title="Vista tabla"
                    >☰</button>
                    <button
                        className={`sl-view-btn ${view === 'grid' ? 'active' : ''}`}
                        onClick={() => setView('grid')}
                        title="Vista grid"
                    >⊞</button>
                    <button className="sl-btn-refresh" onClick={fetchSongs} title="Recargar">
                        🔄
                    </button>
                </div>
            </header>

            {/* ── FILTERS ── */}
            <div className="sl-filters">
                <div className="sl-search-wrap">
                    <span className="sl-search-icon">🔍</span>
                    <input
                        className="sl-search"
                        placeholder="Buscar por título o artista..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <button className="sl-search-clear" onClick={() => setSearch('')}>✕</button>
                    )}
                </div>

                <div className="sl-filter-chips">
                    <span className="sl-filter-label">Dificultad:</span>
                    <button
                        className={`sl-chip ${filterDiff === '' ? 'active' : ''}`}
                        onClick={() => setFilterDiff('')}
                    >
                        Todas
                    </button>
                    {DIFFICULTIES.map(d => (
                        <button
                            key={d}
                            className={`sl-chip ${filterDiff === d ? 'active' : ''}`}
                            style={{ '--cc': DIFF_CFG[d].color }}
                            onClick={() => setFilterDiff(filterDiff === d ? '' : d)}
                        >
                            {DIFF_CFG[d].emoji} {DIFF_CFG[d].label}
                        </button>
                    ))}
                </div>

                <div className="sl-filter-chips">
                    <span className="sl-filter-label">Categoría:</span>
                    <button
                        className={`sl-chip ${filterCat === '' ? 'active' : ''}`}
                        onClick={() => setFilterCat('')}
                    >
                        Todas
                    </button>
                    {CATEGORIES.map(c => (
                        <button
                            key={c}
                            className={`sl-chip ${filterCat === c ? 'active' : ''}`}
                            onClick={() => setFilterCat(filterCat === c ? '' : c)}
                        >
                            {CAT_EMOJI[c]} {c}
                        </button>
                    ))}
                </div>

                {(filterDiff || filterCat || search) && (
                    <div className="sl-results-count">
                        {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                        <button className="sl-clear-all" onClick={() => { setSearch(''); setFilterDiff(''); setFilterCat(''); }}>
                            Limpiar filtros
                        </button>
                    </div>
                )}
            </div>

            {/* ── CONTENT ── */}
            <div className="sl-content">

                {/* Loading */}
                {loading && (
                    <div className="sl-loading">
                        <div className="sl-spinner" />
                        <span>Cargando biblioteca...</span>
                    </div>
                )}

                {/* Empty state */}
                {!loading && filtered.length === 0 && (
                    <div className="sl-empty">
                        <div className="sl-empty-icon">🎵</div>
                        <div className="sl-empty-title">
                            {songs.length === 0 ? 'Biblioteca vacía' : 'Sin resultados'}
                        </div>
                        <div className="sl-empty-sub">
                            {songs.length === 0
                                ? 'Crea tu primera canción desde la pestaña Sync Editor'
                                : 'Intenta con otros filtros de búsqueda'}
                        </div>
                    </div>
                )}

                {/* Table view */}
                {!loading && filtered.length > 0 && view === 'table' && (
                    <div className="sl-table-wrap">
                        <table className="sl-table">
                            <thead>
                                <tr>
                                    <th className="sl-th-thumb">Portada</th>
                                    <th>Título / Artista</th>
                                    <th>Dificultad</th>
                                    <th>Categoría</th>
                                    <th className="sl-th-center">Blancos</th>
                                    <th className="sl-th-center">Líneas</th>
                                    <th>Creado</th>
                                    <th className="sl-th-actions">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(song => (
                                    <SongRow
                                        key={song.id}
                                        song={song}
                                        onEdit={() => setEditSong(song)}
                                        onCorregir={() => corrigeEnElEditor(song)}
                                        abriendo={abriendo === song.id}
                                        onDelete={() => setDeleteSong(song)}
                                        onDuplicate={() => handleDuplicate(song)}
                                        onPublicar={() => handlePublicar(song)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Grid view */}
                {!loading && filtered.length > 0 && view === 'grid' && (
                    <div className="sl-grid">
                        {filtered.map(song => (
                            <SongCard
                                key={song.id}
                                song={song}
                                onEdit={() => setEditSong(song)}
                                onCorregir={() => corrigeEnElEditor(song)}
                                abriendo={abriendo === song.id}
                                onDelete={() => setDeleteSong(song)}
                                onDuplicate={() => handleDuplicate(song)}
                                onPublicar={() => handlePublicar(song)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ── MODALS ── */}
            {editSong && (
                <EditModal
                    song={editSong}
                    token={authToken}
                    onSave={handleSaveEdit}
                    onClose={() => setEditSong(null)}
                    onTranscriptImported={handleTranscriptImported}
                />
            )}

            {deleteSong && (
                <ConfirmModal
                    song={deleteSong}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteSong(null)}
                />
            )}

            {/* ── TOAST ── */}
            {toast.text && (
                <div className={`sl-toast sl-toast--${toast.type}`}>
                    {toast.text}
                </div>
            )}
        </div>
    );
}

// ── Row component ─────────────────────────────────────────────────────
function SongRow({ song, onEdit, onCorregir, abriendo, onDelete, onDuplicate, onPublicar }) {
    const blanks = countBlanks(song.sync_data);
    const lines = song.sync_data?.lines?.length || 0;
    // Sin la marca —una canción de antes de que existiera— se da por publicada,
    // que es como se comportaba. Un `undefined` no puede esconder contenido.
    const publicada = song.is_published !== false;

    return (
        <tr className="sl-tr" style={publicada ? undefined : { opacity: 0.6 }}>
            <td className="sl-td-thumb">
                <div className="sl-thumb-wrap">
                    <img
                        src={thumb(song.youtube_id)}
                        alt={song.title}
                        className="sl-thumb"
                        onError={e => { e.target.style.display = 'none'; }}
                    />
                    <a
                        href={`https://youtu.be/${song.youtube_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="sl-thumb-play"
                    >▶</a>
                </div>
            </td>
            <td>
                <div className="sl-cell-title">
                    {song.title}
                    {!publicada && (
                        <span title="No se ve en el catálogo del alumno"
                              style={{ marginLeft: 6, fontSize: 9, fontWeight: 700,
                                       padding: '1px 6px', borderRadius: 50,
                                       background: 'rgba(148,163,184,.18)',
                                       color: '#94a3b8' }}>
                            sin publicar
                        </span>
                    )}
                </div>
                <div className="sl-cell-sub">{song.artist}</div>
            </td>
            <td><DiffBadge diff={song.difficulty} /></td>
            <td>
                <span className="sl-cat-pill">
                    {CAT_EMOJI[song.category] || '🎶'} {song.category}
                </span>
            </td>
            <td className="sl-td-center">
                <span className="sl-num-badge orange">{blanks}</span>
            </td>
            <td className="sl-td-center">
                <span className="sl-num-badge">{lines}</span>
            </td>
            <td className="sl-td-date">{fmtDate(song.created_at)}</td>
            <td className="sl-td-actions">
                <div className="sl-action-row">
                    <button
                        className="sl-act-btn"
                        onClick={onPublicar}
                        title={publicada
                            ? 'Quitar del catálogo del alumno (no se borra)'
                            : 'Publicar: el alumno podrá verla'}
                        style={{ opacity: publicada ? 1 : 0.55 }}
                    >{publicada ? '🌍' : '🔒'}</button>
                    {/* Dos botones distintos a propósito: el lápiz cambia la
                        ficha —título, artista, categoría— y la varita abre la
                        canción entera en el Sync Editor para corregir sus
                        tiempos y sus blancos. Antes sólo estaba el primero y
                        parecía que lo segundo no se podía hacer. */}
                    <button className="sl-act-btn edit" onClick={onEdit} title="Editar la ficha: título, artista, categoría">✏️</button>
                    <button
                        className="sl-act-btn corregir"
                        onClick={onCorregir}
                        disabled={abriendo}
                        title="Corregir sus tiempos y sus blancos en el Sync Editor"
                    >{abriendo ? '⏳' : '🪄'}</button>
                    <button className="sl-act-btn dup" onClick={onDuplicate} title="Duplicar">📋</button>
                    <button className="sl-act-btn delete" onClick={onDelete} title="Eliminar">🗑️</button>
                </div>
            </td>
        </tr>
    );
}

// ── Card component (grid view) ────────────────────────────────────────
function SongCard({ song, onEdit, onCorregir, abriendo, onDelete, onDuplicate, onPublicar }) {
    const blanks = countBlanks(song.sync_data);
    const cfg = DIFF_CFG[song.difficulty] || DIFF_CFG.intermediate;
    const publicada = song.is_published !== false;

    return (
        <div className="sl-card"
             style={{ '--dc': cfg.color, opacity: publicada ? 1 : 0.6 }}>
            <div className="sl-card-thumb">
                <img
                    src={thumb(song.youtube_id)}
                    alt={song.title}
                    onError={e => { e.target.parentElement.style.background = '#1a1f2e'; }}
                />
                <div className="sl-card-overlay">
                    <button className="sl-act-btn edit" onClick={onEdit}>✏️ Ficha</button>
                    <button className="sl-act-btn corregir" onClick={onCorregir} disabled={abriendo}>
                        {abriendo ? '⏳ Abriendo…' : '🪄 Corregir'}
                    </button>
                    <button className="sl-act-btn" onClick={onPublicar}
                        title={publicada
                            ? 'Quitar del catálogo del alumno (no se borra)'
                            : 'Publicar: el alumno podrá verla'}
                    >{publicada ? '🌍 Publicada' : '🔒 Sin publicar'}</button>
                </div>
            </div>
            <div className="sl-card-body">
                <div className="sl-card-title">{song.title}</div>
                <div className="sl-card-artist">{song.artist}</div>
                <div className="sl-card-meta">
                    <DiffBadge diff={song.difficulty} />
                    <span className="sl-cat-pill small">
                        {CAT_EMOJI[song.category] || '🎶'} {song.category}
                    </span>
                </div>
                <div className="sl-card-stats">
                    <span>{blanks} blancos</span>
                    <span>{song.sync_data?.lines?.length || 0} líneas</span>
                </div>
            </div>
            <div className="sl-card-foot">
                <button className="sl-act-btn dup small" onClick={onDuplicate}>📋</button>
                <button className="sl-act-btn delete small" onClick={onDelete}>🗑️</button>
            </div>
        </div>
    );
}